import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import BudgetKindDonationRow from './BudgetKindDonationRow';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { numberWithCommas } from 'shared/utils/numberUtils';
import { KindDonation, kindDonationsList } from './utils';
import TrueImpactDollarField, {
  dollarAmountDefaultValue,
  dollarAmountTwoDecimalDefaultValue,
  dollarToInt,
} from 'shared/design-system/components/TrueImpactDollarField';

export type BudgetDonationFieldErrors = {
  [id: number]:
    | {
        index: number;
        errorInHoursField?: boolean;
        errorInPerHoursField?: boolean;
      }
    | undefined;
};

interface BudgetDonationsModalContentProps {
  errors: BudgetDonationFieldErrors;
  reportId: number | string;
  total: string;
  handleTotal: (t: string, errors: BudgetDonationFieldErrors) => unknown;
}

const StyledDiv = styled.div`
  display: flex;
  justify-content: center;
  padding: ${SPACERS.large};
  .calculator-grid {
    display: grid;
    gap: 19px ${SPACERS.medium};
    grid-template-columns: 1fr 1fr 1fr 1fr 30px;
    .text-center {
      text-align: center;
      align-self: center;
      justify-self: center;
    }
    .left-column-text {
      text-align: right;
      align-self: center;
      justify-self: end;
    }
  }
  .calculator-info-icon {
    cursor: pointer;
    align-self: center;
    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

const noOp = () => null;
const defaultValue = dollarAmountDefaultValue;
const BudgetDonationsModalContent: React.FC<BudgetDonationsModalContentProps> = ({
  total,
  errors,
  handleTotal,
}) => {
  const [donationsList, setDonationsList] = useState<KindDonation[]>(
    kindDonationsList(),
  );

  useEffect(() => {
    setDonationsList(s => {
      const newState = [...s];
      newState.forEach((d, i) => {
        newState[i].errorInHoursField = errors[i]?.errorInHoursField;
        newState[i].errorInPerHoursField = errors[i]?.errorInPerHoursField;
      });
      return newState;
    });
  }, [errors, setDonationsList]);

  useEffect(() => {
    const errorsList: BudgetDonationFieldErrors = {};
    let total = 0;
    donationsList.forEach((d, index) => {
      if (
        // check this instead, Number(d.hours)
        d.hours &&
        d.hours !== defaultValue &&
        d.hours !== dollarAmountTwoDecimalDefaultValue &&
        (!d.perHour ||
          d.perHour === defaultValue ||
          d.perHour === dollarAmountTwoDecimalDefaultValue)
      ) {
        errorsList[index] = {
          index,
          errorInPerHoursField: true,
        };
      }
      if (
        d.perHour &&
        d.perHour !== defaultValue &&
        d.perHour !== dollarAmountTwoDecimalDefaultValue &&
        (!d.hours ||
          d.hours === defaultValue ||
          d.hours === dollarAmountTwoDecimalDefaultValue)
      ) {
        errorsList[index] = {
          index,
          errorInHoursField: true,
        };
      }
      total += dollarToInt(d.total);
    });
    handleTotal(numberWithCommas(total.toFixed(2)), errorsList);
  }, [donationsList, handleTotal, setDonationsList]);

  return (
    <StyledDiv>
      <div className="calculator-grid">
        <div />
        <TrueImpactText variant="body-bold" className="text-center">
          Hours or units
        </TrueImpactText>
        <TrueImpactText variant="body-bold" className="text-center">
          Value per hour/unit
        </TrueImpactText>
        <TrueImpactText variant="body-bold" className="text-center">
          Total value
        </TrueImpactText>
        <div />
        {donationsList.map((d, i) => (
          <BudgetKindDonationRow
            key={i}
            index={i}
            donation={d}
            setDonationsList={setDonationsList}
          />
        ))}
        <TrueImpactText variant="body-bold" className="left-column-text">
          Total value
        </TrueImpactText>
        <div />
        <div />
        <TrueImpactDollarField
          value={total}
          disabled={true}
          onChange={noOp}
          placeholder="Total value"
        />
      </div>
    </StyledDiv>
  );
};

export default BudgetDonationsModalContent;
